import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import logo from "@/assets/logo.webp";
import { servicesData } from "@/data/services";
import { localSeoPages } from "@/data/localSeoPages";

const HIDDEN_FOOTER_SLUGS = new Set(["it-helpdesk", "website-erstellung"]);
const footerServices = servicesData.filter((s) => !HIDDEN_FOOTER_SLUGS.has(s.slug));

const columnCard =
  "rounded-2xl p-6 bg-background/60 dark:bg-white/5 border border-border/40 dark:border-white/10 backdrop-blur-xl shadow-[0_4px_24px_-8px_rgba(0,0,0,0.15)]";

const linkClass =
  "text-sm text-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1 group";

const Footer = () => {
  return (
    <footer className="relative pt-16 pb-8 overflow-hidden">
      {/* Ambient glow — subtle in light, richer in dark */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

      <div className="relative container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Brand */}
          <div className={columnCard}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-background/60 dark:bg-white/10 border border-border/40 dark:border-white/15 backdrop-blur-xl">
                <img
                  src={logo}
                  alt="ManageMatch Logo"
                  className="h-8 w-auto"
                  width={48}
                  height={32}
                  style={{ mixBlendMode: "multiply" }}
                />
              </div>
              <span className="font-outfit font-bold text-lg text-foreground">
                ManageMatch
              </span>
            </div>
            <p className="text-sm text-foreground/70 leading-relaxed">
              Dienstleistungen smart organisiert — Koordination, Planung und Qualitätsmanagement aus einer Hand.
            </p>
          </div>

          {/* Navigation */}
          <div className={columnCard}>
            <h3 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Navigation</h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className={linkClass}>Startseite</Link>
              <Link to="/dienstleistungen" className={linkClass}>Dienstleistungen</Link>
              <Link to="/digital-it" className={linkClass}>Digital & IT</Link>
              <Link to="/ueber-uns" className={linkClass}>Über uns</Link>
              <Link to="/warum-wir" className={linkClass}>Warum wir?</Link>
              <Link to="/kontakt" className={linkClass}>Kontakt</Link>
            </nav>
          </div>

          {/* Services */}
          <div className={columnCard}>
            <h3 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Dienstleistungen</h3>
            <nav className="flex flex-col gap-2">
              {footerServices.map((service) => (
                <Link
                  key={service.slug}
                  to={`/dienstleistungen/${service.slug}`}
                  className={linkClass}
                >
                  {service.title}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact + Legal */}
          <div className={columnCard}>
            <h3 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Kontakt</h3>
            <div className="space-y-2 text-sm">
              <a href="tel:+4915565412425" className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors">
                <Phone className="w-4 h-4 shrink-0 text-primary dark:text-violet-300" />
                +49 155 65412425
              </a>
              <a href="mailto:support@managematch.services" className="flex items-center gap-2 text-foreground/80 hover:text-foreground transition-colors break-all">
                <Mail className="w-4 h-4 shrink-0 text-primary dark:text-violet-300" />
                support@managematch.services
              </a>
              <div className="flex items-start gap-2 text-foreground/70">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-primary dark:text-violet-300" />
                <span>Marienstr. 18<br />63457 Hanau</span>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-border/40 dark:border-white/10 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <Link to="/impressum" className="text-foreground/60 hover:text-foreground transition-colors">Impressum</Link>
              <Link to="/agb" className="text-foreground/60 hover:text-foreground transition-colors">AGB</Link>
              <Link to="/widerrufsrecht" className="text-foreground/60 hover:text-foreground transition-colors">Widerrufsrecht</Link>
              <Link to="/datenschutz" className="text-foreground/60 hover:text-foreground transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>

        {/* Unsere Leistungen */}
        <div className={`${columnCard} mb-8`}>
          <h3 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">Unsere Leistungen</h3>
          <nav className="flex flex-wrap gap-x-5 gap-y-2">
            {localSeoPages.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} className={linkClass}>{p.h1}</Link>
            ))}
          </nav>
        </div>

        {/* Bottom bar as a single glass pill */}
        <div className="rounded-full px-5 py-3 bg-background/60 dark:bg-white/5 border border-border/40 dark:border-white/10 backdrop-blur-xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground/60">
            © {new Date().getFullYear()} ManageMatch. Alle Rechte vorbehalten.
          </p>
          <Link
            to="/ticket-system/login"
            className="text-xs text-foreground/70 hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            Internes System <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
